import { useEffect, useState } from "react";
import type { HostnameRecord, HostnameStatus } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import { getSession } from "@/lib/session.js";
import {
  listHostnames,
  createHostname,
  deleteHostname,
  refreshHostnameStatus,
} from "@/lib/hostnames-api.js";

function statusColor(status: HostnameStatus): string {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "ssl_pending":
    case "pending":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "error":
    case "blocked":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function statusLabel(status: HostnameStatus): string {
  switch (status) {
    case "active":
      return "Live";
    case "ssl_pending":
      return "Issuing SSL…";
    case "pending":
      return "Awaiting CNAME";
    case "error":
      return "Error";
    case "blocked":
      return "Blocked";
    case "moved":
      return "Moved";
    case "deleted":
      return "Deleted";
    default:
      return status;
  }
}

export function HostnamesPanel({
  onError,
}: {
  onError: (msg: string | undefined) => void;
}) {
  const session = getSession();
  const [hostnames, setHostnames] = useState<HostnameRecord[]>([]);
  const [limit, setLimit] = useState<number | "unlimited" | undefined>();
  const [cnameTarget, setCnameTarget] = useState<string>("cname.agencylens.app");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState<string | undefined>();

  async function reload() {
    if (!session) return;
    try {
      setBusy("Loading hostnames…");
      const data = await listHostnames(session.jwt);
      setHostnames(data.hostnames);
      setLimit(data.limit);
      setCnameTarget(data.cnameTarget);
    } catch (e) {
      const ee = e as Error & { error?: string; hint?: string };
      if (ee.error === "cloudflare_not_configured") {
        // Don't surface as an error — render the panel's not-provisioned state instead
      } else {
        onError(`Could not list hostnames: ${ee.error ?? ee.message}`);
      }
    } finally {
      setBusy(undefined);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.jwt]);

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!session || !input.trim()) return;
    onError(undefined);
    setBusy("Registering hostname…");
    try {
      await createHostname(session.jwt, input.trim());
      setInput("");
      await reload();
    } catch (e) {
      const ee = e as Error & { error?: string; hint?: string };
      onError(`${ee.error ?? ee.message}${ee.hint ? ` — ${ee.hint}` : ""}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onRefresh(id: string) {
    if (!session) return;
    onError(undefined);
    setBusy("Checking status…");
    try {
      const r = await refreshHostnameStatus(session.jwt, id);
      setHostnames((prev) => prev.map((h) => (h.id === id ? r.hostname : h)));
    } catch (e) {
      onError(`Status check failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDelete(rec: HostnameRecord) {
    if (!session) return;
    if (!window.confirm(`Remove ${rec.hostname} from white-label?`)) return;
    onError(undefined);
    setBusy("Removing…");
    try {
      await deleteHostname(session.jwt, rec.id);
      setHostnames((prev) => prev.filter((h) => h.id !== rec.id));
    } catch (e) {
      onError(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  const atLimit =
    limit !== undefined &&
    limit !== "unlimited" &&
    hostnames.length >= limit;

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-lg font-medium">White-label hostnames</h2>
      <p className="mb-4 text-sm text-slate-600">
        Point a subdomain of your agency's domain at AgencyLens so your clients
        see your URL instead of ours. We register the hostname against our
        Cloudflare zone and provision SSL automatically.
      </p>

      {limit !== undefined && (
        <p className="mb-4 text-xs text-slate-500">
          Your tier allows{" "}
          <strong>{limit === "unlimited" ? "unlimited" : limit}</strong>{" "}
          hostname{limit === 1 ? "" : "s"}. Currently used: {hostnames.length}.
        </p>
      )}

      <form onSubmit={onAdd} className="mb-6 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="reports.youragency.com"
          className="min-w-[280px] flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
          disabled={busy !== undefined || atLimit}
        />
        <Button type="submit" disabled={busy !== undefined || !input.trim() || atLimit}>
          Add hostname
        </Button>
      </form>

      {atLimit && (
        <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Tier limit reached. Upgrade for more white-label domains.
        </p>
      )}

      {hostnames.length === 0 ? (
        <p className="text-sm text-slate-500">No white-label hostnames yet.</p>
      ) : (
        <div className="space-y-3">
          {hostnames.map((h) => (
            <div
              key={h.id}
              className="rounded-md border border-slate-200 p-4 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-slate-900">{h.hostname}</span>
                  <span
                    className={`ml-3 inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${statusColor(
                      h.status,
                    )}`}
                  >
                    {statusLabel(h.status)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    onClick={() => onRefresh(h.id)}
                    disabled={busy !== undefined}
                    className="text-brand-700 underline"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={() => onDelete(h)}
                    disabled={busy !== undefined}
                    className="text-red-700 underline"
                  >
                    Remove
                  </button>
                </div>
              </div>
              {(h.status === "pending" || h.status === "error") && (
                <p className="mt-2 rounded bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  Add a CNAME record at your DNS provider:{" "}
                  <code className="font-mono text-slate-900">{h.hostname}</code>{" "}
                  → <code className="font-mono text-slate-900">{cnameTarget}</code>.
                  SSL provisioning may take 1–10 minutes after the CNAME is live.
                </p>
              )}
              {h.lastError && (
                <p className="mt-2 text-xs text-red-700">
                  Last error: {h.lastError}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
